import { Controller } from '@hotwired/stimulus'
import { parseEuro } from '../helpers/currency'

const TOLERANCE = 1e-6;

export default class extends Controller {
  static targets = [
    'submit', 'amount', 'share', 'shareAmount', 'recapShare', 'fieldHint', 'minimumHint',
  ];

  static values = {
    placeholder: { type: String, default: '—' },
  };

  connect() {
    this.sync();
  }

  sync(event) {
    this.#updateMinimum();
    this.#validateAmount();
    this.submitTarget.disabled = !this.element.checkValidity();
    this.#updateValidationHint(event);
    this.#updateShareRecap();
    this.#updateShareAmount();
  }

  #updateMinimum() {
    if (!this.hasAmountTarget) return;

    const { shareMinimumHint, shareTicketHint } = this.#shareData();
    if (shareMinimumHint === undefined) return;

    if (this.hasMinimumHintTarget) this.minimumHintTarget.textContent = shareMinimumHint;
    this.dispatch('minimumChanged', { target: window, detail: { ticketHint: shareTicketHint } });
  }

  #validateAmount() {
    if (!this.hasAmountTarget) return;

    this.amountTarget.setCustomValidity(this.#amountError());
  }

  #amountError() {
    if (this.amountTarget.value === '') return '';

    const amount = parseEuro(this.amountTarget.value);
    const { shareMinimum, shareMinimumError } = this.#shareData();
    const minimum = Number.parseFloat(shareMinimum);
    if (Number.isFinite(minimum) && amount < minimum) return shareMinimumError ?? '';

    return this.#shareMultipleError(amount);
  }

  #shareMultipleError(amount) {
    const { shareAmount, shareMultipleError } = this.#shareData();
    const share = Number.parseFloat(shareAmount);
    if (!(amount > 0) || !Number.isFinite(share) || share <= 0) return '';

    const offBy = Math.abs(amount % share);
    const isMultiple = offBy < TOLERANCE || Math.abs(offBy - share) < TOLERANCE;

    return isMultiple ? '' : shareMultipleError ?? '';
  }

  #shareData() {
    if (!this.hasShareTarget) return this.amountTarget.dataset;

    return this.shareTarget.selectedOptions[0]?.dataset ?? {};
  }

  #updateValidationHint(event) {
    const field = event ? this.#invalidField(event.target) : null;

    this.fieldHintTargets.forEach((hint) => {
      const describes = field !== null && hint.dataset.field === field.name;

      hint.textContent = describes ? field.validationMessage : '';
      hint.classList.toggle('d-none', !describes);
    });
  }

  #invalidField(edited) {
    if (edited?.willValidate && !edited.checkValidity()) return edited;

    return [...this.element.elements].find((field) => field.willValidate && !field.checkValidity()) ?? null;
  }

  #updateShareRecap() {
    if (!this.hasShareTarget || !this.hasRecapShareTarget) return;

    const selected = this.shareTarget.selectedOptions[0];
    this.recapShareTarget.textContent = selected ? selected.text : this.placeholderValue;
  }

  #updateShareAmount() {
    if (!this.hasShareTarget || !this.hasShareAmountTarget) return;

    const selected = this.shareTarget.selectedOptions[0];
    this.shareAmountTarget.textContent = selected?.dataset.shareHint ?? '';
  }
}
